import { Link } from "wouter";
import { TherapistWithSpecialties } from "@shared/schema";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface TherapistCardProps {
  therapist: TherapistWithSpecialties;
  compact?: boolean;
}

export default function TherapistCard({ therapist, compact = false }: TherapistCardProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };
  
  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return "Indisponível";
    
    // Garantir que temos um objeto Date
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Verificar se é uma data válida
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      return "Data inválida";
    }
    
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (dateObj.toDateString() === now.toDateString()) {
      return "Hoje";
    } else if (dateObj.toDateString() === tomorrow.toDateString()) {
      return "Amanhã";
    } else {
      return new Intl.DateTimeFormat('pt-BR', { 
        weekday: 'short'
      }).format(dateObj);
    }
  };
  
  const formatTime = (date: Date | string | null | undefined) => {
    if (!date) return "";
    
    // Garantir que temos um objeto Date
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Verificar se é uma data válida
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      return "";
    }
    
    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(dateObj);
  };
  
  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating / 10);
    const hasHalfStar = rating % 10 >= 5;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={`full-${i}`} className="fas fa-star text-xs text-yellow-400"></i>);
    }
    
    if (hasHalfStar) {
      stars.push(<i key="half" className="fas fa-star-half-alt text-xs text-yellow-400"></i>);
    }
    
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<i key={`empty-${i}`} className="far fa-star text-xs text-yellow-400"></i>);
    }
    
    return stars;
  };
  
  if (compact) {
    return (
      <Card className={`border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-primary transition-colors`}>
        <div className="flex items-center">
          <img 
            src={therapist.imageUrl} 
            alt={therapist.name} 
            className="w-12 h-12 object-cover rounded-full mr-3"
          />
          <div>
            <h4 className="font-medium">{therapist.name}</h4>
            <p className="text-sm text-gray-600">{therapist.title}</p>
          </div>
        </div>
      </Card>
    );
  }
  
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start">
          <img 
            src={therapist.image_url}
            alt={therapist.name} 
            className="w-16 h-16 object-cover rounded-full mr-4"
          />
          <div>
            <h3 className="text-lg font-semibold">{therapist.name}</h3>
            <p className="text-gray-600 text-sm mt-1">{therapist.title}</p>
            <div className="flex items-center mt-1">
              <div className="flex">
                {renderStars(therapist.rating)}
              </div>
              <span className="text-xs text-gray-600 ml-1">
                {(therapist.rating / 10).toFixed(1)} ({therapist.review_count} avaliações)
              </span>
            </div>
          </div>
        </div>
        
        <div className="mt-4">
          <div className="flex flex-wrap gap-2 mb-4">
            {therapist.specialties.map((specialty) => (
              <Badge key={specialty.id} variant="outline" className="bg-primary/10 text-primary text-xs font-normal">
                {specialty.name}
              </Badge>
            ))}
          </div>
          <p className="text-sm text-gray-600 mb-4">
            {therapist.bio}
          </p>
          <div className="flex justify-between">
            <span className="text-dark font-semibold">
              {formatCurrency(therapist.hourly_rate)}
              <span className="text-sm font-normal text-gray-600">/sessão</span>
            </span>
            <Link href={`/therapist/${therapist.id}`} className="text-primary hover:text-secondary font-medium text-sm">
              Ver perfil →
            </Link>
          </div>
        </div>
      </CardContent>
      <CardFooter className="border-t border-gray-100 bg-gray-50 px-6 py-3 flex justify-between">
        <span className="text-sm text-gray-600">
          Próxima disponibilidade: <strong>
            {therapist.next_availability ? `${formatDate(therapist.next_availability)}, ${formatTime(therapist.next_availability)}` : 'Indisponível'}
          </strong>
        </span>
        <Link href={`/booking?therapistId=${therapist.id}`} className="text-primary hover:text-secondary text-sm font-medium">
          Agendar
        </Link>
      </CardFooter>
    </Card>
  );
}
